import { createSelector, Selector } from '@reduxjs/toolkit';

import { AnyMenuItem } from './types';
import { ReduxState } from 'app/redux/types';

import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { detectActiveItem, parseLocationPath } from './utils';

/**
 * Get navigation location state.
 */
export const currentLocation = createSelector(
  (state: ReduxState) => state.router.location.pathname,
  parseLocationPath,
);

export const createAuthItemDetectionSelector = <T extends AnyMenuItem>(
  menuItemsSelector: Selector<ReduxState, T[]>,
) => {
  return createSelector(
    menuItemsSelector,
    selectIsLoggedIn,
    (items, isLoggedIn) => {
      return items.filter((item) => {
        return !item.authOnly || isLoggedIn;
      });
    },
  );
};

export const createActiveItemDetectionSelector = <T extends AnyMenuItem>(
  menuItemsSelector: Selector<ReduxState, T[]>,
  pathname?: string,
) => {
  return createSelector(
    menuItemsSelector,
    currentLocation,
    (items, parsedLocation) => {
      const location = (pathname && parseLocationPath(pathname)) || parsedLocation;

      return detectActiveItem<T>(items, location);
    },
  );
};

export const createAllMatchesCountSelector = <T extends AnyMenuItem>(
  menuItemsSelector: Selector<ReduxState, T[]>,
) => {
  return createSelector(
    menuItemsSelector,
    (items) => {
      const allMatchesCount = items.reduce((acc, item) => {
        if ('entity' in item && 'matchesCount' in item.entity) {
          return acc + item.entity.matchesCount;
        }

        return acc;
      }, 0);

      return allMatchesCount;
    },
  );
};
